import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Sparkles, CheckCircle2, Factory, Users, Wallet, UserCog } from 'lucide-react';
import { useAuth, MODULES } from '@/contexts/AuthContext';

// Tarjeta de estadística general (conteo por módulo)
const StatCard = ({ title, value, isLoading, Icon, gradient, onClick }) => (
  <motion.div
    whileHover={{ y: -4 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    onClick={onClick}
    className={`rounded-2xl text-white shadow-lg p-6 flex items-center justify-between gap-4 ${gradient} ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div>
      <p className="text-sm uppercase tracking-wide opacity-80">{title}</p>
      <h3 className="text-3xl font-bold mt-2">
        {isLoading ? (
          <span className="inline-block h-8 w-14 rounded bg-white/30 animate-pulse" />
        ) : (
          (value ?? 0).toLocaleString('es-CO')
        )}
      </h3>
    </div>
    <div className="bg-white/20 p-3 rounded-xl">
      <Icon className="h-8 w-8 text-white" />
    </div>
  </motion.div>
);

// Pequeño subcomponente para las características
const FeatureCard = ({ title, text }) => (
  <motion.div
    whileHover={{ y: -4, scale: 1.02 }}
    transition={{ duration: 0.3, ease: 'easeOut' }}
    className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-md rounded-xl p-5 border border-indigo-100 dark:border-indigo-700 hover:border-indigo-400 dark:hover:border-indigo-500 transition-all shadow-sm hover:shadow-md"
  >
    <div className="flex items-start gap-3">
      <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400 mt-1 flex-shrink-0" />
      <div>
        <h3 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-1">
          {title}
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{text}</p>
      </div>
    </div>
  </motion.div>
);

const DashboardPage = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const [counts, setCounts] = useState({ plantas: 0, empleados: 0, centrosCosto: 0, usuarios: 0 });
  const [loadingCounts, setLoadingCounts] = useState({ plantas: true, empleados: true, centrosCosto: true, usuarios: true });

  const STAT_CARDS = [
    {
      key: 'plantas',
      title: 'Plantas activas',
      module: MODULES.PLANTAS,
      Icon: Factory,
      gradient: 'bg-gradient-to-br from-[#024281] to-[#024281]',
      path: '/parametrizacion/plantas',
    },
    {
      key: 'empleados',
      title: 'Empleados activos',
      module: MODULES.EMPLOYEES,
      Icon: Users,
      gradient: 'bg-gradient-to-br from-[#7ac143] to-[#7ac143]',
      path: '/parametrizacion/empleados',
    },
    {
      key: 'centrosCosto',
      title: 'Centros de costos',
      module: MODULES.CENTROS_COSTO,
      Icon: Wallet,
      gradient: 'bg-gradient-to-br from-[#f59e0b] to-[#f59e0b]',
      path: '/parametrizacion/centros-costo',
    },
    {
      key: 'usuarios',
      title: 'Usuarios activos',
      module: MODULES.USERS,
      Icon: UserCog,
      gradient: 'bg-gradient-to-br from-[#6366f1] to-[#6366f1]',
      path: '/parametrizacion/usuarios',
    },
  ];

  const visibleStatCards = STAT_CARDS.filter((card) => hasPermission(card.module));

  useEffect(() => {
    if (hasPermission(MODULES.PLANTAS)) {
      (async () => {
        try {
          const { plantasService } = await import('@/pages/parametrizacion/plantas/utils/plantasService');
          const response = await plantasService.getAll();
          const plantas = response?.data || [];
          const activas = plantas.filter((p) => p.estado !== 'Inactivo').length;
          setCounts((prev) => ({ ...prev, plantas: activas }));
        } catch (error) {
          console.error('Error cargando conteo de plantas:', error);
        } finally {
          setLoadingCounts((prev) => ({ ...prev, plantas: false }));
        }
      })();
    } else {
      setLoadingCounts((prev) => ({ ...prev, plantas: false }));
    }

    if (hasPermission(MODULES.EMPLOYEES)) {
      (async () => {
        try {
          const { fetchEmployees } = await import('@/pages/parametrizacion/employees/Services/employees.services');
          const response = await fetchEmployees({ limit: 1, offset: 1, query: { estado: 'Activo' } });
          setCounts((prev) => ({ ...prev, empleados: response?.totalRecords || 0 }));
        } catch (error) {
          console.error('Error cargando conteo de empleados:', error);
        } finally {
          setLoadingCounts((prev) => ({ ...prev, empleados: false }));
        }
      })();
    } else {
      setLoadingCounts((prev) => ({ ...prev, empleados: false }));
    }

    if (hasPermission(MODULES.CENTROS_COSTO)) {
      (async () => {
        try {
          const { centrosCostoService } = await import('@/pages/parametrizacion/centrosCosto/utils/centrosCostoService');
          const response = await centrosCostoService.getWithFilter({ limit: 1, offset: 1, query: { estado: 'Activo' } });
          setCounts((prev) => ({ ...prev, centrosCosto: response?.totalRecords || 0 }));
        } catch (error) {
          console.error('Error cargando conteo de centros de costo:', error);
        } finally {
          setLoadingCounts((prev) => ({ ...prev, centrosCosto: false }));
        }
      })();
    } else {
      setLoadingCounts((prev) => ({ ...prev, centrosCosto: false }));
    }

    if (hasPermission(MODULES.USERS)) {
      (async () => {
        try {
          const { fetchUsers } = await import('@/pages/parametrizacion/usuarios/Services/usuarios.services');
          const response = await fetchUsers({ limit: 1, offset: 1, query: { estado: 'Activo' } });
          setCounts((prev) => ({ ...prev, usuarios: response?.totalRecords || 0 }));
        } catch (error) {
          console.error('Error cargando conteo de usuarios:', error);
        } finally {
          setLoadingCounts((prev) => ({ ...prev, usuarios: false }));
        }
      })();
    } else {
      setLoadingCounts((prev) => ({ ...prev, usuarios: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-10"
    >

      {/* Conteos generales por módulo */}
      {visibleStatCards.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {visibleStatCards.map((card) => (
            <StatCard
              key={card.key}
              title={card.title}
              value={counts[card.key]}
              isLoading={loadingCounts[card.key]}
              Icon={card.Icon}
              gradient={card.gradient}
              onClick={() => navigate(card.path)}
            />
          ))}
        </motion.div>
      )}

      {/* Noticia PROFLUX */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card className="relative overflow-hidden bg-gradient-to-br from-blue-50/60 to-indigo-50/70 dark:from-slate-800/80 dark:to-slate-900/80 border border-indigo-100 dark:border-indigo-800 shadow-lg hover:shadow-2xl transition-all duration-500 rounded-2xl">
          <CardHeader>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-3">
              <div className="flex items-center gap-3">
                <Sparkles className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-pulse" />
                <CardTitle className="text-2xl md:text-3xl font-extrabold bg-gradient-to-r from-blue-700 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent leading-tight">
                  ¡PROFLUX es nuestro nuevo aliado estratégico!
                </CardTitle>
              </div>
              <div className="w-32 h-16 bg-white dark:bg-slate-700 rounded-lg shadow-md flex items-center justify-center border border-gray-200 dark:border-gray-600 overflow-hidden">
                <img
                  src="/logo-proflux.png"
                  alt="Proflux Logo"
                  className="max-w-full max-h-full object-contain p-2"
                />
              </div>
            </div>
            <CardDescription className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">
              Una alianza que transformará la gestión de lavandería industrial.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <p className="text-lg text-slate-800 dark:text-slate-200 leading-relaxed">
              En <span className="font-bold text-blue-600 dark:text-blue-400">PROFLUX</span>,
              entendemos que tu negocio tiene prioridades más importantes que gestionar una lavandería industrial.
              Nuestro objetivo es liberarte de esta carga para que puedas concentrarte en lo que realmente importa.
            </p>

            <div className="grid gap-4 md:grid-cols-3">
              <FeatureCard
                title="Calidad de Lavado"
                text="Utilizamos productos de alta calidad y procesos optimizados."
              />
              <FeatureCard
                title="Rentabilidad"
                text="Reducimos tus costos operativos con una gestión eficiente."
              />
              <FeatureCard
                title="Productividad"
                text="Mejoramos los tiempos y la eficacia de tus procesos de lavandería."
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default DashboardPage;
